import React, { useState, useEffect, useMemo } from 'react';
import Modal from './Modal';
import { MODULES, PERMISSIONS_STRUCTURE } from '../../App';

function UserFormModal({ isOpen, onClose, onSave, initialData, isLoading, showAppModal, locations }) {
    const defaultData = useMemo(() => {
        const permissions = {};
        for (const modKey in PERMISSIONS_STRUCTURE) {
            permissions[modKey] = {};
            for (const pKey in PERMISSIONS_STRUCTURE[modKey]) {
                permissions[modKey][pKey] = false;
            }
        }
        return {
            name: '', email: '', role: 'operador', password: '', confirmPassword: '',
            location: '',
            permissions,
            accountStatus: 'activo', 
        };
    }, []);
    
    const [formData, setFormData] = useState(defaultData);

    useEffect(() => {
        if (!['bodega', 'produccion'].includes(formData.role)) {
            setFormData(prev => ({ ...prev, location: '' }));
        }
    }, [formData.role]);


    useEffect(() => {
        if (isOpen) {
            if (initialData) {
                const mergedPermissions = { ...defaultData.permissions };
                for (const modKey in mergedPermissions) {
                    if (initialData.permissions && initialData.permissions[modKey]) {
                        mergedPermissions[modKey] = { ...mergedPermissions[modKey], ...initialData.permissions[modKey] };
                    }
                }
                // Si el usuario existente no tiene 'accountStatus', se le asigna 'activo' por defecto.
                const finalData = { 
                    ...defaultData, 
                    ...initialData, 
                    permissions: mergedPermissions, 
                    accountStatus: initialData.accountStatus || 'activo',
                    password: '', 
                    confirmPassword: '' 
                };
                setFormData(finalData);
            } else {
                setFormData(defaultData);
            }
        }
    }, [initialData, isOpen, defaultData]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePermissionChange = (moduleKey, permKey, isChecked) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                [moduleKey]: {
                    ...prev.permissions[moduleKey],
                    [permKey]: isChecked
                }
            }
        }));
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();
        if (!initialData && formData.password !== formData.confirmPassword) {
            if(showAppModal) showAppModal("Error de Contraseña", "Las contraseñas no coinciden.");
            return;
        }
        if (['bodega', 'produccion'].includes(formData.role) && !formData.location) {
            if(showAppModal) showAppModal("Campo Requerido", "Debe seleccionar una ubicación para este rol.");
            return;
        }
        onSave(formData);
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Editar Usuario" : "Nuevo Usuario"}>
            <form onSubmit={handleSubmit}>
                <div className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Nombre Completo</label>
                            <input type="text" name="name" value={formData.name} onChange={handleChange} required className="input-field mt-1"/>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Correo Electrónico</label>
                            <input type="email" name="email" value={formData.email} onChange={handleChange} required disabled={!!initialData} className="input-field mt-1 disabled:bg-gray-100"/>
                        </div>
                        {!initialData && <>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Contraseña</label>
                                <input type="password" name="password" value={formData.password} onChange={handleChange} required className="input-field mt-1"/>
                            </div>
                             <div>
                                <label className="block text-sm font-medium text-gray-700">Confirmar Contraseña</label>
                                <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} required className="input-field mt-1"/>
                            </div>
                        </>}
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Rol</label>
                             <select name="role" value={formData.role} onChange={handleChange} className="input-field mt-1 bg-white">
                                <option value="administrador">Administrador</option>
                                <option value="operador">Operador</option>
                                <option value="bodega">Bodega</option>
                                <option value="produccion">Producción</option>
                                <option value="contabilidad">Contabilidad</option>
                                <option value="supervisor">Supervisor</option>
                             </select>
                        </div>
                        {['bodega', 'produccion'].includes(formData.role) && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Ubicación / Bodega</label>
                                <select name="location" value={formData.location} onChange={handleChange} required className="input-field mt-1 bg-white">
                                    <option value="">-- Seleccione una ubicación --</option>
                                    {locations.map(loc => (
                                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        {/* --- CAMBIO: Campo para gestionar el estado del usuario --- */}
                        {initialData && (
                           <div>
                                <label className="block text-sm font-medium text-gray-700">Estado de la Cuenta</label>
                                <select 
                                    name="accountStatus" 
                                    value={formData.accountStatus} 
                                    onChange={handleChange} 
                                    className="input-field mt-1 bg-white"
                                >
                                    <option value="activo">Activo</option>
                                    <option value="suspendido">Suspendido</option>
                                </select>
                           </div>
                        )}
                    </div>
                    <div className="border-t pt-4">
                        <h3 className="text-lg font-semibold text-gray-700">Permisos</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mt-4">
                            {Object.entries(PERMISSIONS_STRUCTURE).map(([modKey, perms]) => (
                                <div key={modKey} className="border p-3 rounded-lg">
                                    <h4 className="font-semibold text-gray-600 mb-2">{MODULES[modKey] || modKey}</h4>
                                    <div className="space-y-2">
                                        {Object.entries(perms).map(([permKey, permLabel]) => (
                                            <label key={`${modKey}-${permKey}`} className="flex items-center">
                                                <input type="checkbox"
                                                    checked={formData.permissions[modKey]?.[permKey] || false}
                                                    onChange={(e) => handlePermissionChange(modKey, permKey, e.target.checked)}
                                                    className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
                                                />
                                                <span className="ml-2 text-sm text-gray-700">{permLabel}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end space-x-3 pt-6 border-t mt-6">
                    <button type="button" onClick={onClose} disabled={isLoading} className="btn-secondary">Cancelar</button>
                    <button type="submit" disabled={isLoading} className="btn-primary">{isLoading ? "Guardando..." : "Guardar Cambios"}</button>
                </div>
            </form>
        </Modal>
    );
}

export default UserFormModal;